import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import { practices } from "../data/practices";

export default function Practice() {
  const location = useLocation();
  const pathSegments = location.pathname.split("/");
  const titleFromURL = decodeURIComponent(pathSegments[pathSegments.length - 1]);

  // Find the practice from the URL or default to the first one
  const defaultPractice = practices.find((practice) => practice.title === titleFromURL) || practices[0];

  const [selectedTitle, setSelectedTitle] = useState(defaultPractice.title);
  const [content, setContent] = useState(defaultPractice.content);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    const foundPractice = practices.find((practice) => practice.title === titleFromURL);
    if (foundPractice) {
      setSelectedTitle(foundPractice.title);
      setContent(foundPractice.content);
    }
  }, [titleFromURL]);

  return (
    <div className="w-full mt-[10%] mx-auto p-6 flex flex-col md:flex-row gap-10">
      {/* Left Side - Tabs / Dropdown for Mobile */}
      <motion.div
        className="w-full md:w-[20%] bg-gray-100 p-6 rounded-lg shadow-lg"
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h3 className="text-xl font-bold text-[#0F172A] mb-4">Practice Areas</h3>
        
        {/* Dropdown for Mobile */}
        <div className="md:hidden relative">
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-white text-gray-700 text-left"
          >
            {selectedTitle}
          </button>
          {isDropdownOpen && (
            <motion.ul
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute w-full mt-2 bg-white border border-gray-300 shadow-lg rounded-lg z-10"
            >
              {practices.map((data, index) => (
                <li
                  key={index}
                  onClick={() => {
                    setSelectedTitle(data.title);
                    setContent(data.content);
                    setIsDropdownOpen(false);
                  }}
                  className="px-4 py-3 cursor-pointer hover:bg-gray-200"
                >
                  {data.title}
                </li>
              ))}
            </motion.ul>
          )}
        </div>

        {/* Tabs for Desktop */}
        <div className="hidden md:flex flex-col space-y-2">
          {practices.map((data, index) => (
            <motion.button
              key={index}
              onClick={() => {
                setSelectedTitle(data.title);
                setContent(data.content);
              }}
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
              className={`text-left px-4 py-3 rounded-lg border ${
                selectedTitle === data.title
                  ? "bg-goldenYellow text-[#0F172A] font-bold shadow-lg"
                  : "border-gray-300 text-gray-700 hover:bg-gray-200"
              } transition duration-300`}
            >
              {data.title}
            </motion.button>
          ))}
        </div>
      </motion.div>

      {/* Right Side - Description */}
      <motion.div
        className="w-full md:w-[80%] bg-white p-8 rounded-lg shadow-lg border border-gray-200"
        key={selectedTitle}
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6 }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedTitle}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl font-bold text-[#0F172A] mb-4 relative">
              {selectedTitle}
              <motion.div
                className="h-1 bg-goldenYellow absolute left-0 bottom-[-6px]"
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ duration: 0.8, ease: "easeInOut" }}
              ></motion.div>
            </h2>

            {/* Handle structured JSON data */}
            {Array.isArray(content) ? (
              content.map((section, idx) => (
                <div key={idx} className="mb-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{section.heading}</h3>
                  {typeof section.details === "string" ? (
                    <p className="text-gray-700">{section.details}</p>
                  ) : (
                    <ul className="list-disc ml-6 text-gray-700">
                      {section.details.map((item, index) => (
                        <li key={index}>
                          <strong>{item.title}: </strong> {item.description}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))
            ) : (
              <p className="text-gray-700">{content}</p>
            )}
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
